import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, User, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import api from "../lib/api";

export default function Header() {
  const { cartCount, setCartOpen, wishlist } = useShop();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [mobile, setMobile] = useState(false);
  const [categories, setCategories] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  // Show up to 2 categories in the top nav (with a "Shop" super-link); rest live in Shop filter
  const featuredCats = categories.slice(0, 2);
  const links = [
    { to: "/shop", label: "Shop" },
    ...featuredCats.map(c => ({ to: `/shop?category=${encodeURIComponent(c.name)}`, label: c.name })),
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const brand = settings.brand_name || "GOSHI";

  const Brand = () =>
    settings.logo_url ? (
      <img
        src={settings.logo_url}
        alt={brand}
        className="h-10 md:h-12 w-auto object-contain select-none"
        style={{ imageRendering: "-webkit-optimize-contrast" }}
        draggable={false}
      />
    ) : (
      <span className="font-serif text-2xl md:text-3xl tracking-[0.35em] text-text">{brand}</span>
    );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-[rgba(244,244,240,0.06)]" style={{ background: "rgba(12,12,12,0.78)" }}>
      <div className="px-6 md:px-12 h-20 flex items-center justify-between gap-4">
        {/* LEFT: Logo (clickable → home) */}
        <Link to="/" className="flex items-center shrink-0" data-testid="brand-logo">
          <Brand />
        </Link>

        {/* CENTER: Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* RIGHT: Account / Wishlist / Cart / Mobile menu */}
        <div className="flex items-center gap-5 md:gap-6 shrink-0">
          <Link to="/wishlist" className="relative text-text hover:text-gold transition-colors" data-testid="wishlist-link">
            <Heart size={18} strokeWidth={1.5} />
            {wishlist.length > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] text-gold">{wishlist.length}</span>
            )}
          </Link>
          <button onClick={() => user ? nav("/account") : nav("/login")} className="text-text hover:text-gold transition-colors" data-testid="account-link">
            <User size={18} strokeWidth={1.5} />
          </button>
          <button onClick={() => setCartOpen(true)} className="relative text-text hover:text-gold transition-colors" data-testid="cart-btn">
            <ShoppingBag size={18} strokeWidth={1.5} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] text-gold" data-testid="cart-count">{cartCount}</span>
            )}
          </button>
          <button className="md:hidden text-text" onClick={() => setMobile(true)} data-testid="mobile-menu-btn">
            <Menu size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {mobile && (
        <div className="fixed inset-0 z-50 bg-[#0C0C0C] flex flex-col">
          <div className="h-20 flex items-center justify-between px-6 border-b border-[rgba(244,244,240,0.06)]">
            <Link to="/" onClick={() => setMobile(false)} className="flex items-center">
              <Brand />
            </Link>
            <button onClick={() => setMobile(false)}><X size={22} strokeWidth={1.5} /></button>
          </div>
          <nav className="flex flex-col items-center justify-center flex-1 gap-8">
            {links.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobile(false)} className="font-serif text-3xl text-text hover:text-gold">
                {l.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
